import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Comment, CommentType } from "../../utils/types";
import { useWorkspaces } from "../workspaces";

interface Props {}

interface CommentsMap {
  [uuid: string]: Comment[];
}

export type ContextValue = {
  comments: CommentsMap;
  setComments: React.Dispatch<React.SetStateAction<CommentsMap>>;
  fetchComments: (type: CommentType, typeUuid: string) => Promise<void>;
  addComment: (
    message: string,
    type: CommentType,
    typeUuid: string
  ) => Promise<ApiResponse<Comment>>;
  updateComment: (
    message: string,
    type: CommentType,
    typeUuid: string,
    commentUuid: string
  ) => Promise<ApiResponse<Comment>>;
  removeComment: (
    uuid: string,
    type: CommentType,
    typeUuid: string
  ) => Promise<ApiResponse<[]>>;
};

export const CommentsContext = React.createContext<ContextValue | undefined>(
  undefined
);

export const CommentsProvider: React.FC<Props> = (props) => {
  const { currentWorkspaceId } = useWorkspaces();
  const [comments, setComments] = useState<CommentsMap>({});
  const location = useLocation();

  const detailsPageMatch = matchPath<{
    tab: string; // hotspots, locations, hexes
    id: string;
    section: string;
    subsection?: string;
  }>(location.pathname, {
    path: ["/:tab/:id/:section/:subsection", "/:tab/:id/:section"]
  });

  const fetchComments = useCallback(
    async (type: CommentType, typeUuid: string) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/comments/${type}/${typeUuid}`
      );

      const apiResponse: ApiResponse<Comment[]> = await response.json();

      if (apiResponse.status) {
        setComments((prevState) => ({
          ...prevState,
          [typeUuid]: apiResponse.data || []
        }));
      }
    },
    [currentWorkspaceId]
  );

  useEffect(() => {
    let type: CommentType | undefined;

    switch (detailsPageMatch?.params.tab) {
      case "locations":
        type = "location";
        break;
      case "hotspots":
        type = "hotspot";
        break;
    }

    if (
      type &&
      detailsPageMatch?.params.id &&
      comments[detailsPageMatch.params.id] == null &&
      detailsPageMatch?.params.id !== "makers"
    ) {
      fetchComments(type, detailsPageMatch.params.id);
    }
  }, [
    comments,
    detailsPageMatch?.params.id,
    detailsPageMatch?.params.tab,
    fetchComments
  ]);

  const addComment = useCallback(
    async (message: string, type: CommentType, typeUuid: string) => {
      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/comments`,
          {
            method: "POST",
            body: JSON.stringify({ message, type, type_uuid: typeUuid })
          }
        );

        const apiResponse: ApiResponse<Comment> = await response.json();

        await fetchComments(type, typeUuid);

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [fetchComments, currentWorkspaceId]
  );

  const updateComment = useCallback(
    async (
      message: string,
      type: CommentType,
      typeUuid: string,
      commentUuid: string
    ) => {
      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/comments/${commentUuid}`,
          {
            method: "PUT",
            body: JSON.stringify({ message })
          }
        );

        const apiResponse: ApiResponse<Comment> = await response.json();

        await fetchComments(type, typeUuid);

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [fetchComments, currentWorkspaceId]
  );

  const removeComment = useCallback(
    async (commentUuid: string, type: CommentType, typeUuid: string) => {
      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/comments/${commentUuid}`,
          {
            method: "DELETE"
          }
        );

        const apiResponse: ApiResponse<[]> = await response.json();

        await fetchComments(type, typeUuid);

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [fetchComments, currentWorkspaceId]
  );

  const value = useMemo(
    () => ({
      comments,
      setComments,
      fetchComments,
      addComment,
      removeComment,
      updateComment
    }),
    [comments, fetchComments, addComment, updateComment, removeComment]
  );

  return <CommentsContext.Provider value={value} {...props} />;
};

export const useComments = (): ContextValue => {
  const context = useContext(CommentsContext);

  if (context === undefined) {
    throw new Error("useComments must be used within an CommentsProvider");
  }

  return context;
};
